import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';
import { useUser } from '../hooks/useUser';
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher';
import { legalDocumentPathForUiLocale, normalizeUiLocale } from '../legal/documents';

export const DashboardLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { handleLogout } = useLogout();
  const { user } = useUser();
  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);

  return (
    <div className="min-h-screen bg-background flex flex-col">

      {/* Top Bar Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* App Title / Logo */}
            <div className="flex items-center">
              <h1 className="text-xl text-foreground">{t('app.title')}</h1>
            </div>

            {/* Right Side: User, Language, Logout */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2">
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground ">
                  {user?.name ? user.name.charAt(0).toUpperCase() : t('dashboard.header.fallback_initial')}
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-foreground">{user?.name || t('dashboard.header.fallback_name')}</p>
                </div>
              </div>

              <div className="scale-90 origin-right">
                <LanguageSwitcher />
              </div>

              <button
                onClick={handleLogout}
                className="text-sm text-destructive hover:text-destructive/80 font-medium flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span className="hidden sm:inline">{t('auth.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav
            aria-label={t('legal.documents_nav')}
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-4"
          >
            <a
              href={legalDocumentPathForUiLocale('privacy', uiLocale)}
              className="text-sm text-foreground underline underline-offset-2 hover:text-muted-foreground"
            >
              {t('legal.privacy')}
            </a>
            <a
              href={legalDocumentPathForUiLocale('terms', uiLocale)}
              className="text-sm text-foreground underline underline-offset-2 hover:text-muted-foreground"
            >
              {t('legal.terms')}
            </a>
          </nav>
          <p className="text-center text-muted-foreground text-sm">
            {t('app.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};